import { useMemo } from 'react';

import { GeneratorOutput } from '../../../hooks/useGeneratorOutput';
import { SegmentGeneratorFn } from '../SegmentGeneratorFn';
import { PointTransition, PointTransitionOutput } from './useCustomPointTransition';

interface UseCustomPathInterpolationProps<TDataKeyType, TPointType, TDatum> {
  /**
   * Whether the data has changed
   */
  dataChanged: boolean;
  /**
   * The associated point produced by the generator.
   */
  generatorOutput: GeneratorOutput<TDataKeyType, TPointType, TDatum>;
  /**
   * Whether the shape should be animated
   */
  isAnimated: boolean;
  /**
   * Transition point value series by series key
   */
  pointTransitionsByKey: PointTransitionOutput<TDataKeyType>;
  /**
   * Segment Generation function to generate initial and transitional segments from point transitions
   */
  generateSegmentTransition: SegmentGeneratorFn;
}
/**
 * Create custom intermediate segmented svg path strings between 2 paths that have different number of vertices
 * The intermediate paths looks as their start/end counter parts but are segmented and include dummy points so that point
 * orders don't change during interpolation
 */
export const useCustomPathInterpolation = <TDataKeyType, TPointType, TDatum>({
  dataChanged,
  generatorOutput,
  isAnimated,
  pointTransitionsByKey,
  generateSegmentTransition,
}: UseCustomPathInterpolationProps<TDataKeyType, TPointType, TDatum>): Map<
  TDataKeyType,
  [initialSegmentedPath: string, transitionSegmentedPath: string]
> => {
  const segmentedPathKeyMap = useMemo(() => {
    const segmentedPathsByKey = new Map<
      TDataKeyType,
      [initialSegmentedPath: string, transitionSegmentedPath: string]
    >();

    if (!isAnimated || !dataChanged) {
      return segmentedPathsByKey;
    }

    generatorOutput.forEach((dataSeries) => {
      const customPointTransitions = Array.from(pointTransitionsByKey.get(dataSeries.key)?.values() ?? []);
      if (customPointTransitions.length > 0) {
        let segmentedInitialPath = '';
        let segmentedTransitionPath = '';
        const pointTransitionsInSegments = customPointTransitions.reduce<PointTransition[][]>((acc, point) => {
          if (acc[point.segmentIndex]) {
            acc[point.segmentIndex].push(point);
          } else {
            // start of a new segment
            acc[point.segmentIndex] = [point];
            if (point.segmentIndex > 0) {
              // add the point to previous segment to complete the segment
              acc[point.segmentIndex - 1].push(point);
            }
          }
          return acc;
        }, []);

        for (let i = 0; i < pointTransitionsInSegments.length; i += 1) {
          const pointTransitionsSegment = pointTransitionsInSegments[i];
          const [initialPath, transitionPath] = generateSegmentTransition(pointTransitionsSegment);
          segmentedInitialPath += initialPath;
          segmentedTransitionPath += transitionPath;
        }
        segmentedPathsByKey.set(dataSeries.key, [segmentedInitialPath, segmentedTransitionPath]);
      }
    });

    return segmentedPathsByKey;
  }, [generatorOutput, pointTransitionsByKey, generateSegmentTransition, dataChanged, isAnimated]);

  return segmentedPathKeyMap;
};
